<template>
  <div class="tutorial-view">
    <div class="relative-box">
      <svg :width="width" :height="height" :viewBox="viewBox" @click="() => nextStep()">
        <rect x="0" y="0" fill="rgba(0, 0, 0, 0.3)" :width="width" :height="height" mask="url(#hole)" />
        <mask id="hole">
          <rect x="0" y="0" :width="width" :height="height" fill="white" />
          <rect :x="mask.x" :y="mask.y" :width="mask.width" :height="mask.height" fill="black" />
        </mask>
      </svg>
      <div
        :class="['message-container', direction]"
        :style="{
          left: anchor.left,
          top: anchor.top,
        }"
      >
        <div class="message-box">
          <div class="message-content">
            <div v-if="currentStep === -1" class="default-message">
              <h3>{{ $t('welcome') }}</h3>
              <p class="baseline">{{ $t('tutorial.baseline') }}</p>
              <button class="study-button main" @click="() => nextStep()">{{ $t('begin') }}</button>
              <button class="study-button secondary" @click="() => skipTutorial()">{{ $t('tutorial.skip') }}</button>
            </div>
            <div v-else class="default-message">
              <p v-if="currentScenarioStep">
                {{ $t(`tutorial.${currentScenarioStep.key}`) }}
              </p>
              <button class="study-button main" @click="() => nextStep()">
                {{ currentStep === scenario.length - 1 ? $t('understood') : $t('next') }}
              </button>
            </div>
          </div>
          <div v-if="currentStep > -1" class="bullet-container">
            <div v-for="(bullet, idx) in scenario" :key="idx" :class="['bullet', { active: idx <= currentStep }]" @click.stop="() => updateStep(idx)"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import scenario from '@/components/Tutorial/scenario';

export default {
  data() {
    return {
      scenario,
      currentStep: -1,
      width: window.innerWidth,
      height: window.innerHeight,
      direction: 'default',
      mask: {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      },
    };
  },

  computed: {
    currentScenarioStep() {
      return this.scenario[this.currentStep];
    },

    viewBox() {
      const { width, height } = this;
      return `0 0 ${width} ${height}`;
    },

    anchor() {
      const { mask } = this;
      if (this.direction === 'right') {
        return {
          left: `${mask.x + mask.width}px`,
          top: `${mask.y + mask.height / 2}px`,
        };
      } else if (this.direction === 'bottom') {
        return {
          left: `${mask.x + mask.width / 2}px`,
          top: `${mask.y + mask.height}px`,
        };
      } else if (this.direction === 'bottom-left') {
        return {
          left: `${mask.x}px`,
          top: `${mask.y + mask.height}px`,
        };
      } else {
        return {
          left: `50%`,
          top: `50%`,
        };
      }
    },
  },

  mounted() {
    this.updateMask();
    window.addEventListener('resize', this.handleResize, false);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    skipTutorial() {
      this.$store.dispatch('updateTutorial', true);
    },

    updateStep(step) {
      this.currentStep = step || -1;
      this.updateMask();
    },

    nextStep() {
      if (this.currentStep < scenario.length - 1) {
        this.currentStep++;
        this.updateMask();
      } else {
        this.$store.dispatch('updateTutorial', true);
      }
    },

    handleResize() {
      this.width = window.innerWidth;
      this.height = window.innerHeight;
      this.updateMask();
    },

    updateMask() {
      const { currentStep } = this;
      if (currentStep > -1) {
        const $el = this.$root.$el.querySelector(scenario[currentStep].target);
        if ($el) {
          this.direction = scenario[currentStep].direction;
          this.mask = this.mapBBox($el.getBoundingClientRect());
        }
      } else {
        this.direction = 'center';
        this.mask = {
          x: 0,
          y: 0,
          width: 0,
          height: 0,
        };
      }
    },

    mapBBox(BBox) {
      const { left, top, width, height } = BBox;
      return {
        x: left,
        y: top,
        width: width,
        height: height,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';

.tutorial-view {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 100;
}
.relative-box {
  position: relative;
  height: 100%;
}
.default-message {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  user-select: none;
}
h3 {
  font-size: 16px;
  font-weight: 800;
  color: #3c3c3b;
  &:after {
    content: '';
    display: block;
    box-sizing: border-box;
    height: 2px;
    width: 50px;
    background-color: #bc141a;
    margin: 16px auto;
  }
}
p {
  max-width: 100%;
  margin: 0;
  color: #3c3c3b;
  text-align: center;
  &.baseline {
    font-size: 14px;
  }
}
.message-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transition: all 0.3s ease;
  min-height: 30px;
  width: 300px;

  &:before {
    content: '';
    display: block;
    width: 16px;
    height: 16px;
    background-color: white;
    box-sizing: border-box;
    z-index: 0;
    position: absolute;
  }
  &.center {
    transform: translateX(-50%) translateY(-50%);
    left: 50%;
    top: 50%;
    &:before {
      visibility: hidden;
    }
  }
  &.right {
    padding-left: 12px;
    transform: translateY(-50%);
    &:before {
      left: 4px;
      top: 50%;
      margin-top: -8px;
      transform: rotate(45deg);
      border-left: 1px solid #e3e3e3;
      border-bottom: 1px solid #e3e3e3;
    }
  }
  &.bottom {
    padding-top: 12px;
    transform: translateX(-50%);
    &:before {
      left: 50%;
      top: 4px;
      margin-left: -8px;
      transform: rotate(45deg);
      border-left: 1px solid #e3e3e3;
      border-top: 1px solid #e3e3e3;
    }
  }
  &.bottom-left {
    padding-top: 12px;
    transform: translateX(-50%);
    &:before {
      left: 80%;
      top: 4px;
      margin-left: -8px;
      transform: rotate(45deg);
      border-left: 1px solid #e3e3e3;
      border-top: 1px solid #e3e3e3;
    }
  }

  .message-box {
    background-color: white;
    box-sizing: border-box;
    border-radius: 4px;
    border: 1px solid #e3e3e3;
    box-shadow: 0 2px 2px rgba(0, 0, 0, 0.3);
    min-height: 82px;
    display: flex;
    flex-direction: column;
  }
  .message-content {
    flex: 1;
    padding: 28px;

    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
      flex: none;
    }
  }
  .bullet-container {
    height: 30px;
    background-color: #f2f2f2;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    .bullet {
      background-color: #d1caca;
      height: 8px;
      width: 8px;
      border-radius: 4px;
      margin-left: 4px;
      margin-right: 4px;
      cursor: pointer;
      &.active {
        background-color: #bc141a;
      }
    }
  }
  .study-button {
    margin-top: 16px;
    height: 30px;
    padding: 0 20px;
    &.secondary {
      margin-top: 8px;
    }
  }
}
</style>
